import { Phone, Mail, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { ADDRESS_LINE1, ADDRESS_LINE2, BOOKING_URL, EMAIL, GOOGLE_REVIEWS_URL, NAV_LINKS, PHONE_DISPLAY, PHONE_TEL, PRINCIPAL, SERVICES } from "@/data";
import { useSectionNav } from "@/components/Nav";
import { NavItem } from "@/components/NavItem";
import { track } from "@/hooks/useAnalytics";

const Heading = ({ children }) => (
  <p className="font-mono text-xs tracking-[0.25em] uppercase text-leaf">{children}</p>
);

const Brand = () => (
  <div className="lg:col-span-4">
    <img src="/logo.svg" alt="Glebe Park Dental" className="h-12 w-auto" width="394" height="132" />
    <p className="mt-5 max-w-xs font-sans text-sm leading-relaxed text-fern">
      Your friendly city dentist on Coranderrk Street, next to Glebe Park. Looking after Canberra smiles since 1989 with {PRINCIPAL} and Dr Peter Stejskal.
    </p>
    <div className="mt-6 flex items-center gap-3">
      <span className="pulse-dot h-2 w-2 rounded-full bg-leaf" />
      <span className="font-sans text-xs text-ink/80">New patients welcome</span>
    </div>
  </div>
);

const ServiceLinks = () => (
  <div className="lg:col-span-2">
    <Heading>Services</Heading>
    <ul className="mt-5 space-y-2.5">
      {SERVICES.map((s) => (
        <li key={s.slug}>
          <Link to={`/services/${s.slug}`} data-testid={`footer-service-link-${s.slug}`} className="font-sans text-sm text-fern transition-colors hover:text-forest">
            {s.name}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const ExploreLinks = () => {
  const go = useSectionNav();
  return (
    <div className="lg:col-span-2">
      <Heading>Explore</Heading>
      <ul className="mt-5 space-y-2.5">
        {NAV_LINKS.map((l) => (
          <li key={l.label}>
            <NavItem link={l} onSection={go} testid={`footer-link-${l.id || l.to.slice(1)}`} className="font-sans text-sm text-fern transition-colors hover:text-forest" />
          </li>
        ))}
      </ul>
    </div>
  );
};

const VisitBook = () => (
  <div className="lg:col-span-4">
    <Heading>Visit & Book</Heading>
    <div className="mt-5 space-y-3 font-sans text-sm text-fern">
      <p className="flex items-start gap-3">
        <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-leaf" />
        <span>{ADDRESS_LINE1}, {ADDRESS_LINE2}</span>
      </p>
      <a href={PHONE_TEL} data-testid="footer-phone-link" className="flex items-center gap-3 transition-colors hover:text-forest">
        <Phone className="h-4 w-4 text-leaf" /> {PHONE_DISPLAY}
      </a>
      <a href={`mailto:${EMAIL}`} className="flex items-center gap-3 transition-colors hover:text-forest">
        <Mail className="h-4 w-4 text-leaf" /> {EMAIL}
      </a>
    </div>
    <a
      href={BOOKING_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="footer-book-now-button"
      onClick={() => track("book_click", { location: "footer-book-now-button" })}
      className="mt-6 inline-flex items-center gap-2 rounded-md bg-forest px-6 py-3 font-sans text-sm font-medium text-cream transition-[background-color,box-shadow] duration-300 hover:bg-leafdeep hover:shadow-[0_8px_30px_-6px_rgba(36,54,47,0.5)]"
    >
      Book Online
    </a>
    <p className="mt-5 max-w-xs font-sans text-xs leading-relaxed text-fern/70">
      Toothache? Ring us first thing — we keep room in the day for people in pain and we'll do our best to see you same-day.
    </p>
  </div>
);

const Footer = () => (
  <footer className="border-t border-stone/70 bg-sage py-16 lg:py-20">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-16">
      <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-12">
        <Brand />
        <ServiceLinks />
        <ExploreLinks />
        <VisitBook />
      </div>
      <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-stone/70 pt-8 sm:flex-row">
        <p className="font-sans text-xs text-fern/60">© {new Date().getFullYear()} Glebe Park Dental · {PRINCIPAL} · Canberra ACT</p>
        <div className="flex flex-col items-center gap-3 sm:flex-row sm:gap-6">
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-google-reviews-link"
            className="font-sans text-xs text-fern/80 underline decoration-leaf/50 underline-offset-4 transition-colors hover:text-forest"
          >
            See our reviews on Google
          </a>
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-fern/70">Mon–Sat 9:00am — 5:30pm</p>
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
